import React from 'react'

import { Tooltip } from '@mui/material'

import { useCanAccess } from 'hooks'
import { Permissions } from 'types'
import { permissionLabel } from 'utils/permissionLabels'

// VRG-677: the role editor's own label ("ai_view" → "View AI"), not a humanized slug
// ("Ai view"), so the tooltip names something the admin can actually find (§27).
const defaultTooltip = (permission?: Permissions) =>
  permission
    ? `You need the “${permissionLabel(permission)}” permission. Ask an admin to grant it.`
    : 'You don’t have permission to do this. Ask an admin for access.'

interface PermissionGateProps {
  /** The permission required to use the wrapped action. */
  permission?: Permissions
  /**
   * How to treat the lack of access:
   * - `disable` (default): render the child disabled + a tooltip explaining
   *   which permission is needed. Use for **in-context actions** (a button or
   *   menu item on a surface the user can already see) — keeps the UI consistent
   *   across roles and gives a path to request access.
   * - `hide`: render nothing. Use for **whole surfaces / cross-tier nav** the
   *   user shouldn't even know exist (e.g. internal/admin nav for a company user).
   * See the access-gating convention in `context/working-conventions.md`.
   */
  mode?: 'disable' | 'hide'
  /** Override the disabled-state tooltip copy. */
  tooltip?: React.ReactNode
  /**
   * Either a single interactive element (Button / MenuItem / etc.) — which is
   * cloned with `disabled` in disable mode — or a render-prop
   * `(allowed) => ReactNode` for full control (e.g. gating a non-standard child).
   */
  children: React.ReactElement | ((allowed: boolean) => React.ReactNode)
}

/**
 * Gate an action on a permission. Default behaviour is "show it, disabled, with a
 * tooltip" (the better UX for in-context actions); pass `mode="hide"` for surfaces
 * that should be invisible to users without access.
 */
export const PermissionGate = ({
  permission,
  mode = 'disable',
  tooltip,
  children,
}: PermissionGateProps) => {
  const { isHasAccess } = useCanAccess({ action: permission })

  // Render-prop escape hatch: hand the caller the boolean.
  if (typeof children === 'function') return <>{children(isHasAccess)}</>

  if (isHasAccess) return children

  if (mode === 'hide') return null

  // disable mode: clone the child as disabled + wrap in a tooltip. A disabled
  // element emits no hover events, so the span is the tooltip's hover target.
  const disabledChild = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<any>, { disabled: true })
    : children

  return (
    <Tooltip title={tooltip ?? defaultTooltip(permission)} arrow>
      <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>{disabledChild}</span>
    </Tooltip>
  )
}
